import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, FilePlus2, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { parseDateOnly } from "@/lib/utils";

const _empty_person = { full_name: "", role: "", years_experience: "", cert_name: "", cert_number: "", cert_expires: "", bio: "" };
const _empty_ref = { project_name: "", client: "", contract_number: "", value: "", pop_start: "", pop_end: "",
  trades: "", scope_summary: "", poc_name: "", poc_org: "", poc_email: "", poc_phone: "" };

const isExpired = (d) => { const dt = parseDateOnly(d); if (!dt) return false; const t = new Date(); return dt < new Date(t.getFullYear(), t.getMonth(), t.getDate()); };

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] text-zinc-500">
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid} className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 text-zinc-800" />
    </label>
  );
}

export function KeyPersonnelPanel() {
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [p, setP] = useState(_empty_person);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/brain/personnel").then((x) => setPersonnel(x.data.personnel)).catch(() => setPersonnel([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!p.full_name.trim()) return toast.error("Full name is required.");
    setBusy(true);
    try {
      await api.post("/brain/personnel", { full_name: p.full_name, role: p.role, years_experience: p.years_experience,
        bio: p.bio, certifications: p.cert_name ? [{ name: p.cert_name, number: p.cert_number, expires: p.cert_expires }] : [] });
      toast.success("Person added to the Brain.");
      setP(_empty_person); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="key-personnel-panel">
      <div className="border border-zinc-200 rounded-xl p-4 bg-white">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-zinc-700 mb-3"><UserPlus className="h-4 w-4" /> Add a person (WD staff)</div>
        <div className="space-y-2">
          <Field label="Full name *" value={p.full_name} onChange={(v) => setP({ ...p, full_name: v })} testid="person-name" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Role" value={p.role} onChange={(v) => setP({ ...p, role: v })} testid="person-role" />
            <Field label="Years experience" value={p.years_experience} onChange={(v) => setP({ ...p, years_experience: v })} testid="person-years" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Cert name" value={p.cert_name} onChange={(v) => setP({ ...p, cert_name: v })} placeholder="CTS-I" testid="person-cert" />
            <Field label="Cert #" value={p.cert_number} onChange={(v) => setP({ ...p, cert_number: v })} testid="person-certnum" />
            <Field label="Expires" type="date" value={p.cert_expires} onChange={(v) => setP({ ...p, cert_expires: v })} testid="person-certexp" />
          </div>
          <button onClick={save} disabled={busy} data-testid="person-save"
            className="text-[12px] bg-gold-dark text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
            {busy ? "Saving…" : "Add to Brain"}
          </button>
        </div>
      </div>
      <div className="space-y-2" data-testid="personnel-list">
        {personnel.length === 0 && <div className="text-[12px] text-zinc-400 p-4">No WD personnel yet — the engine will gap any required certified role.</div>}
        {personnel.map((x) => (
          <div key={x.id} className="border border-zinc-200 rounded-lg p-3 bg-white text-[12px]" data-testid="person-row">
            <div className="font-semibold text-zinc-800">{x.full_name} <span className="font-normal text-zinc-500">· {x.role} · {x.years_experience} yrs</span></div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(x.certifications || []).map((c, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${isExpired(c.expires) ? "bg-red-50 text-red-600 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                  {isExpired(c.expires) ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                  {c.name}{c.number ? ` #${c.number}` : ""}{c.expires ? ` · ${isExpired(c.expires) ? "EXPIRED" : c.expires}` : ""}
                </span>
              ))}
              {(x.certifications || []).length === 0 && <span className="text-zinc-400">no certs</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PastPerformancePanel() {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [r, setR] = useState(_empty_ref);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/brain/references").then((x) => setRefs(x.data.references)).catch(() => setRefs([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!r.project_name.trim()) return toast.error("Project name is required.");
    setBusy(true);
    try {
      await api.post("/brain/references", { ...r, trades: r.trades.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Reference added to the Brain.");
      setR(_empty_ref); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="past-performance-panel">
      <div className="border border-zinc-200 rounded-xl p-4 bg-white">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-zinc-700 mb-3"><FilePlus2 className="h-4 w-4" /> Add a reference</div>
        <div className="space-y-2">
          <Field label="Project name *" value={r.project_name} onChange={(v) => setR({ ...r, project_name: v })} testid="ref-project" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Client" value={r.client} onChange={(v) => setR({ ...r, client: v })} testid="ref-client" />
            <Field label="Contract #" value={r.contract_number} onChange={(v) => setR({ ...r, contract_number: v })} testid="ref-contract" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Value" value={r.value} onChange={(v) => setR({ ...r, value: v })} placeholder="$250,000" testid="ref-value" />
            <Field label="Start" type="date" value={r.pop_start} onChange={(v) => setR({ ...r, pop_start: v })} testid="ref-start" />
            <Field label="End" type="date" value={r.pop_end} onChange={(v) => setR({ ...r, pop_end: v })} testid="ref-end" />
          </div>
          <Field label="Trades (comma-separated)" value={r.trades} onChange={(v) => setR({ ...r, trades: v })} placeholder="audiovisual, low-voltage" testid="ref-trades" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="POC name" value={r.poc_name} onChange={(v) => setR({ ...r, poc_name: v })} testid="ref-pocname" />
            <Field label="POC email (external)" value={r.poc_email} onChange={(v) => setR({ ...r, poc_email: v })} testid="ref-pocemail" />
          </div>
          <button onClick={save} disabled={busy} data-testid="ref-save"
            className="text-[12px] bg-gold-dark text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
            {busy ? "Saving…" : "Add to Brain"}
          </button>
        </div>
      </div>
      <div className="space-y-2" data-testid="references-list">
        {refs.length === 0 && <div className="text-[12px] text-zinc-400 p-4">No references yet — proposals ship the honest thin capability statement.</div>}
        {refs.map((x) => (
          <div key={x.id} className="border border-zinc-200 rounded-lg p-3 bg-white text-[12px]" data-testid="ref-row">
            <div className="font-semibold text-zinc-800">{x.project_name} <span className="font-normal text-zinc-500">· {x.client} · {x.value}</span></div>
            <div className="text-zinc-500 mt-0.5">{x.contract_number} · {x.pop_start}–{x.pop_end} · {(x.trades || []).join(", ")}</div>
            <div className="text-zinc-500">POC: {x.poc_name} · {x.poc_email}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
